import React, { useState, useEffect, useRef } from 'react';
import api, { API_BASE_URL } from '../utils/api';
import { User, Mail, Phone, MapPin, Calendar, Edit3, Save, X, Upload, Camera, Shield, Crown, Eye, Edit, Lock, EyeOff, CheckCircle, AlertCircle, Clock, Linkedin } from 'lucide-react';

// Utility function to generate user initials
const getUserInitials = (name) => {
  if (!name) return 'U';
  
  const nameParts = name.trim().split(' ');
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }
  
  // Return first letter of first name + first letter of last name
  return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
};

// Utility function to format 24-hour time to 12-hour format
const formatTo12Hour = (time24) => {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':');
  const hour12 = parseInt(hours);
  const displayHour = hour12 === 0 ? 12 : hour12 > 12 ? hour12 - 12 : hour12;
  const period = hour12 >= 12 ? 'PM' : 'AM';
  
  return `${displayHour}:${minutes} ${period}`;
};

const UserProfile = ({ user, onClose, onUserUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    bio: '',
    job_title: '',
    company: '',
    linkedin: '',
    timezone: '',
    avatar_url: '',
    work_hours: {
      start: '09:00',
      end: '17:00',
      work_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    }
  });
  
  // Store the last successfully fetched profile data for cancel operations
  const [lastFetchedProfileData, setLastFetchedProfileData] = useState({});
  
  const [teamInfo, setTeamInfo] = useState(null);
  const [userRole, setUserRole] = useState('viewer');
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    // Only fetch profile data once when component mounts, not when user prop changes
    fetchUserProfile();
    fetchWorkHours();
    fetchTeamInfo();
    
    // Check for stored event details from App.js
    if (window.userProfileEventDetails) {
      const details = window.userProfileEventDetails;
      if (details.showPasswordSection) {
        setShowPasswordSection(true);
      }
      if (details.startEditing) {
        setIsEditing(true);
      }
      // Clear the stored details
      window.userProfileEventDetails = null;
    }
    
    // Also listen for future events
    const handleUserProfileEvent = (event) => {
      if (event.detail?.showPasswordSection) {
        setShowPasswordSection(true);
      }
      if (event.detail?.startEditing) {
        setIsEditing(true);
      }
    };
    
    window.addEventListener('openUserProfile', handleUserProfileEvent);
    
    return () => {
      window.removeEventListener('openUserProfile', handleUserProfileEvent);
    };
  }, []); // Remove [user] dependency to prevent auto-refresh

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/users/profile');
      
      // Use only the API response data, don't merge with existing form state
      const fetchedData = response.data;
      
      setProfileData(fetchedData);
      setLastFetchedProfileData(fetchedData); // Store for cancel operations
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      if (error.response?.status === 401) {
        alert('Authentication expired. Please log in again.');
      } else {
        alert(`Failed to load profile: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkHours = async () => {
    try {
      const response = await api.get('/users/work-hours');
      
      if (response.data) {
        // Convert API format to component format
        const workHours = {
          start: response.data.work_hours_start?.substring(0, 5) || '09:00',
          end: response.data.work_hours_end?.substring(0, 5) || '17:00',
          work_days: response.data.work_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
        };
        
        setProfileData(prev => ({ 
          ...prev, 
          work_hours: workHours,
          timezone: response.data.timezone || prev.timezone
        }));
        setLastFetchedProfileData(prev => ({ 
          ...prev, 
          work_hours: workHours,
          timezone: response.data.timezone || prev.timezone
        }));
      }
    } catch (error) {
      console.error('Failed to fetch work hours:', error);
      // Use defaults if fetch fails
    }
  };

  const fetchTeamInfo = async () => {
    try {
      const response = await api.get('/team');
      setTeamInfo(response.data);
      // Find current user's role
      const currentMember = response.data.members?.find(member => member.user.id === user.id);
      if (currentMember) {
        setUserRole(currentMember.role);
      }
    } catch (error) {
      console.error('Failed to fetch team info:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      console.log('Saving profile data:', profileData); // Debug log
      
      // Save main profile data (excluding work_hours as it's handled separately)
      const profileDataToSave = { ...profileData };
      delete profileDataToSave.work_hours; // Remove work_hours from profile save
      
      // Don't send avatar_url if it's empty - let the backend keep the existing value
      if (!profileDataToSave.avatar_url || profileDataToSave.avatar_url.trim() === '') {
        delete profileDataToSave.avatar_url;
      }
      
      const response = await api.put('/users/profile', profileDataToSave);

      console.log('Save response:', response.data); // Debug log
      
      // Save work hours separately
      if (profileData.work_hours) {
        const workHoursData = {
          work_hours_start: profileData.work_hours.start + ':00', // Convert HH:MM to HH:MM:SS
          work_hours_end: profileData.work_hours.end + ':00',
          work_days: profileData.work_hours.work_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          timezone: profileData.timezone || 'America/New_York'
        };
        
        await api.put('/users/work-hours', workHoursData);
        
        console.log('Work hours saved successfully'); // Debug log
      }
      
      // Update both current and last fetched data with saved data
      const updatedData = { ...response.data, work_hours: profileData.work_hours, timezone: profileData.timezone };
      setProfileData(updatedData);
      setLastFetchedProfileData(updatedData);
      setIsEditing(false);
      
      // Update user in parent component
      if (onUserUpdate) {
        onUserUpdate(response.data);
      }
      
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      
      if (error.response?.status === 401) {
        alert('Authentication expired. Please log in again.');
      } else if (error.response?.status === 422) {
        alert(`Validation error: ${error.response?.data?.detail || 'Invalid data provided'}`);
      } else {
        alert(`Failed to update profile: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Revert to the last successfully fetched data instead of user prop
    setProfileData({ ...lastFetchedProfileData });
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMessage({ type: '', text: '' });

    // Validate passwords
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      setPasswordLoading(false);
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      setPasswordLoading(false);
      return;
    }

    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setShowPasswordSection(false);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setPasswordMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to change password' 
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    // Clear error messages when user starts typing
    if (passwordMessage.type === 'error') {
      setPasswordMessage({ type: '', text: '' });
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }

    try {
      setUploadingImage(true);
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/users/upload-avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update profile data with new avatar URL
      const newAvatarUrl = response.data.avatar_url;
      console.log('New avatar URL:', newAvatarUrl);
      setProfileData(prev => ({ 
        ...prev, 
        avatar_url: newAvatarUrl 
      }));
      
      // Force a re-fetch to ensure we have the latest data
      await fetchUserProfile();
      
      alert('Avatar updated successfully!');
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      alert(`Failed to upload avatar: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploadingImage(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getRoleBadge = () => {
    const roleConfig = {
      owner: { color: 'bg-purple-100 text-purple-800', icon: Crown },
      admin: { color: 'bg-blue-100 text-blue-800', icon: Shield },
      editor: { color: 'bg-green-100 text-green-800', icon: Edit3 },
      viewer: { color: 'bg-gray-100 text-gray-800', icon: Eye }
    };
    
    const config = roleConfig[userRole] || roleConfig.viewer;
    const Icon = config.icon;
    
    return (
      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        <span>{userRole.charAt(0).toUpperCase() + userRole.slice(1)}</span>
      </div>
    );
  };

  // Inline mode (when onClose is null) - used in Settings page
  const isInline = onClose === null;

  if (loading) {
    if (isInline) {
      return (
        <div className="w-full">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      );
    }
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-2xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // The profile content wrapper
  const profileContent = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Profile' : 'User Profile'}
          </h2>
          {getRoleBadge()}
        </div>
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all shadow-md"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          ) : (
            <div className="text-sm text-gray-500 cursor-pointer" onClick={() => setIsEditing(true)}>
              Click here to edit profile
            </div>
          )}
          {!isInline && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors relative z-20"
              disabled={saving}
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      {/* Profile Content */}
      <div className="p-6 space-y-8">
          {/* Avatar and Basic Info */}
          <div className="flex items-start space-x-6">
            <div className="relative">
              <div className="w-32 h-32 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                {(() => {
                  const showImage = profileData.avatar_url && 
                                   profileData.avatar_url.trim() !== '' && 
                                   !profileData.avatar_url.includes('example.com');
                  console.log('Avatar display check:', { 
                    avatar_url: profileData.avatar_url, 
                    showImage 
                  });
                  return showImage ? (
                    <img 
                      src={profileData.avatar_url} 
                      alt={profileData.name} 
                      className="w-32 h-32 rounded-full object-cover"
                      onError={(e) => {
                        console.error('Image failed to load:', profileData.avatar_url);
                        // Hide broken image and show initials
                        e.target.style.display = 'none';
                        const initialsSpan = e.target.nextElementSibling;
                        if (initialsSpan) {
                          initialsSpan.style.display = 'block';
                        }
                      }}
                      onLoad={() => {
                        console.log('Image loaded successfully:', profileData.avatar_url);
                      }}
                    />
                  ) : null;
                })()}
                <span 
                  className="text-white font-bold text-4xl"
                  style={{ display: profileData.avatar_url && profileData.avatar_url.trim() !== '' && !profileData.avatar_url.includes('example.com') ? 'none' : 'block' }}
                >
                  {getUserInitials(profileData.name)}
                </span>
              </div>
              {isEditing && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <button 
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="absolute bottom-2 right-2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {uploadingImage ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                    ) : (
                      <Camera className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                </>
              )}
            </div>

            <div className="flex-1 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="Enter your full name"
                  />
                ) : (
                  <p className="text-xl font-semibold text-gray-900">{profileData.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  {isEditing ? (
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      placeholder="Enter your email"
                    />
                  ) : (
                    <span className="text-gray-900">{profileData.email}</span>
                  )}
                </div>
              </div>

              {/* Job Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.job_title}
                    onChange={(e) => handleInputChange('job_title', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="e.g. Senior Developer, Project Manager"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.job_title || 'Not specified'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                {isEditing ? (
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="Enter your phone number"
                  />
                ) : (
                  <span className="text-gray-900">{profileData.phone || 'Not provided'}</span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="e.g. New York, NY"
                  />
                ) : (
                  <span className="text-gray-900">{profileData.location || 'Not specified'}</span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
              {isEditing ? (
                <input
                  type="text"
                  value={profileData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                  placeholder="Enter your company name"
                />
              ) : (
                <p className="text-gray-900">{profileData.company || 'Not specified'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
              {isEditing ? (
                <select
                  value={profileData.timezone}
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                >
                  <option value="">Select timezone</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Europe/Paris">Paris (CET)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Australia/Sydney">Sydney (AEDT)</option>
                </select>
              ) : (
                <p className="text-gray-900">{profileData.timezone || 'Not specified'}</p>
              )}
            </div>

            {/* Work Hours Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Work Hours</label>
              {isEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Start Time</label>
                      <input
                        type="time"
                        value={profileData.work_hours?.start || '09:00'}
                        onChange={(e) => handleInputChange('work_hours', {
                          ...profileData.work_hours,
                          start: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {profileData.work_hours?.start ? formatTo12Hour(profileData.work_hours.start) : '9:00 AM'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">End Time</label>
                      <input
                        type="time"
                        value={profileData.work_hours?.end || '17:00'}
                        onChange={(e) => handleInputChange('work_hours', {
                          ...profileData.work_hours,
                          end: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {profileData.work_hours?.end ? formatTo12Hour(profileData.work_hours.end) : '5:00 PM'}
                      </div>
                    </div>
                  </div>
 
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Work Days</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: 'monday', label: 'Mon' },
                        { key: 'tuesday', label: 'Tue' },
                        { key: 'wednesday', label: 'Wed' },
                        { key: 'thursday', label: 'Thu' },
                        { key: 'friday', label: 'Fri' },
                        { key: 'saturday', label: 'Sat' },
                        { key: 'sunday', label: 'Sun' }
                      ].map(day => (
                        <button
                          key={day.key}
                          type="button"
                          onClick={() => {
                            const workDays = profileData.work_hours?.work_days || [];
                            const newWorkDays = workDays.includes(day.key)
                              ? workDays.filter(d => d !== day.key)
                              : [...workDays, day.key];
                            handleInputChange('work_hours', {
                              ...profileData.work_hours,
                              work_days: newWorkDays
                            });
                          }}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                            profileData.work_hours?.work_days?.includes(day.key)
                              ? 'bg-purple-100 border-purple-300 text-purple-700'
                              : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-gray-900">
                    {formatTo12Hour(profileData.work_hours?.start || '09:00')} - {formatTo12Hour(profileData.work_hours?.end || '17:00')}
                  </p>
                  <p className="text-sm text-gray-600">
                    Days: {profileData.work_hours?.work_days?.length > 0 
                      ? profileData.work_hours.work_days.map(day => 
                          day.charAt(0).toUpperCase() + day.slice(1, 3)
                        ).join(', ')
                      : 'Mon-Fri'
                    }
                  </p>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2 text-gray-700">
                <Linkedin className="w-4 h-4" />
                <label className="block text-sm font-medium">LinkedIn</label>
              </div>
              {isEditing ? (
                <input
                  type="url"
                  value={profileData.linkedin}
                  onChange={(e) => handleInputChange('linkedin', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                  placeholder="https://www.linkedin.com/in/your-handle"
                />
              ) : (
                profileData.linkedin ? (
                  <a
                    href={profileData.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:underline break-all"
                  >
                    {profileData.linkedin}
                  </a>
                ) : (
                  <p className="text-gray-900">Not provided</p>
                )
              )}
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
            {isEditing ? (
              <textarea
                value={profileData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                placeholder="Tell us a bit about yourself..."
              />
            ) : (
              <p className="text-gray-900">{profileData.bio || 'No bio provided'}</p>
            )}
          </div>

          {/* Password Change Section */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Lock className="w-5 h-5" />
                <span>Password & Security</span>
              </h3>
              {!showPasswordSection ? (
                <button
                  onClick={() => setShowPasswordSection(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Change Password</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowPasswordSection(false);
                    setPasswordData({
                      current_password: '',
                      new_password: '',
                      confirm_password: ''
                    });
                    setPasswordMessage({ type: '', text: '' });
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Password Change Messages */}
            {passwordMessage.text && (
              <div className={`mb-4 p-3 rounded-lg flex items-center space-x-2 ${
                passwordMessage.type === 'success' 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {passwordMessage.type === 'success' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span>{passwordMessage.text}</span>
              </div>
            )}

            {showPasswordSection ? (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      required
                      value={passwordData.current_password}
                      onChange={(e) => handlePasswordInputChange('current_password', e.target.value)}
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showNewPassword ? "text" : "password"}
                      required
                      value={passwordData.new_password}
                      onChange={(e) => handlePasswordInputChange('new_password', e.target.value)}
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      placeholder="Enter new password (min 6 chars)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      value={passwordData.confirm_password}
                      onChange={(e) => handlePasswordInputChange('confirm_password', e.target.value)}
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    {passwordLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Changing...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Change Password</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswordData({
                        current_password: '',
                        new_password: '',
                        confirm_password: ''
                      });
                      setPasswordMessage({ type: '', text: '' });
                    }}
                    disabled={passwordLoading}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <p className="text-gray-600">Change your account password for enhanced security.</p>
            )}
          </div>

          {/* Team Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Team</p>
                <p className="font-medium text-gray-900">{teamInfo?.team?.name || 'Loading...'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Role</p>
                <div className="mt-1">
                  {getRoleBadge()}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Member Since</p>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Team Members</p>
                <p className="font-medium text-gray-900">{teamInfo?.members?.length || 0} members</p>
              </div>
            </div>
          </div>
        </div>
    </>
  );
  
  // Render inline or as modal based on onClose prop
  if (isInline) {
    // Inline mode for Settings page
    return (
      <div className="w-full">
        {profileContent}
      </div>
    );
  }
  
  // Modal mode for profile dropdown
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto relative z-10" onClick={(e) => e.stopPropagation()}>
        {profileContent}
      </div>
    </div>
  );
};

export default UserProfile;
